/**
 * GA4 & Meta Pixel Tracking Service for Frontend
 * Centralized tracking for all conversion events
 */

// Meta Pixel IDs
const META_PIXEL_IDS = {
  DH: "238260475866409", // Digital Health
  AP: "831231161987100", // AP Region
};

// GA4 Configuration from GTM
const GA4_CONFIG = {
  MEASUREMENT_ID: "G-0RW157KYY4",
  GOOGLE_ADS_ID: "AW-10788349731",
};

/**
 * Track Add to Cart event on GA4 and Meta Pixel
 * @param {Object} productData - Product information
 * @param {string} productData.product_id - Product ID
 * @param {string} productData.product_name - Product name
 * @param {string} productData.brand_id - Brand name (optional)
 * @param {string} productData.category_id - Category name (optional)
 * @param {number} productData.sp - Sale price
 * @param {number} productData.mrp - MRP/Regular price
 * @param {number} quantity - Quantity added
 * @param {string} currencyCode - Currency code (default: INR)
 */
export const trackAddToCart = (productData, quantity = 1, currencyCode = "INR") => {
  if (!productData || !productData.product_id) {
    console.warn("TrackingService: Invalid product data", productData);
    return;
  }

  // Determine the price to use
  const price = productData.sp > 0 ? productData.sp : productData.mrp;
  const totalValue = price * quantity;

  // Build product item object
  const productItem = {
    id: String(productData.product_id) || "unknown",
    name: productData.product_name || "Unknown Product",
    brand: productData.brand_name || "",
    category: productData.category_name || "",
    price: parseFloat(price) || 0,
    quantity: parseInt(quantity) || 1,
    variant: productData.variant || null,
  };

  // Get user data if available
  const userData = getUserData();

  // Track GA4 Event
  trackGA4AddToCart(productItem, totalValue, currencyCode);

  // Track Meta Pixel Event
  trackMetaPixelAddToCart(productItem, totalValue, currencyCode, userData);

  console.log("✓ Add to Cart tracked:", {
    product: productItem,
    quantity,
    totalValue,
    currency: currencyCode,
  });
};

/**
 * Track event to GA4 via GTM dataLayer
 */
const trackGA4AddToCart = (productItem, totalValue, currencyCode) => {
  try {
    // Push to dataLayer for GTM/GA4
    if (window.dataLayer) {
      window.dataLayer.push({
        event: "add_to_cart",
        ecommerce: {
          currency: currencyCode,
          value: totalValue,
          items: [
            {
              item_id: productItem.id,
              item_name: productItem.name,
              item_brand: productItem.brand,
              item_category: productItem.category,
              price: productItem.price,
              quantity: productItem.quantity,
            },
          ],
        },
      });
    }

    // Also track via gtag if available (for Google Ads)
    if (window.gtag) {
      window.gtag("event", "add_to_cart", {
        value: totalValue,
        currency: currencyCode,
        items: [
          {
            id: productItem.id,
            name: productItem.name,
            brand: productItem.brand,
            category: productItem.category,
            price: productItem.price,
            quantity: productItem.quantity,
          },
        ],
      });

      // Also send to Google Ads for conversion tracking
      window.gtag("event", "conversion", {
        send_to: GA4_CONFIG.GOOGLE_ADS_ID,
        value: totalValue,
        currency: currencyCode,
      });
    }

    console.log("✓ GA4 Event Sent:", {
      event: "add_to_cart",
      value: totalValue,
    });
  } catch (error) {
    console.error("GA4 Tracking Error:", error);
  }
};

/**
 * Track event to Meta Pixel
 */
const trackMetaPixelAddToCart = (productItem, totalValue, currencyCode, userData) => {
  try {
    if (!window.fbq) {
      console.warn("Meta Pixel not initialized");
      return;
    }

    // Prepare Meta Pixel item object
    const metaItem = {
      id: productItem.id,
      name: productItem.name,
      brand: productItem.brand || "",
      category: productItem.category || "",
      price: productItem.price,
      quantity: productItem.quantity,
      variant: productItem.variant || "",
    };

    // Build Meta Pixel add_to_cart event
    const pixelEventData = {
      value: totalValue,
      currency: currencyCode,
      contents: [metaItem],
      content_name: productItem.name,
      content_category: productItem.category || "Product",
      content_type: "product",
      content_ids: [productItem.id],
      num_items: productItem.quantity,
      ...(userData.email && { email: userData.email }),
      ...(userData.phone && { phone_number: userData.phone }),
    };

    // Add user data if available
    if (Object.keys(userData).length > 0) {
      pixelEventData.user_data = {
        email: userData.email,
        phone_number: userData.phone,
        address: {
          first_name: userData.first_name,
          last_name: userData.last_name,
        },
      };
    }

    // Send to all configured Meta pixels
    Object.values(META_PIXEL_IDS).forEach((pixelId) => {
      try {
        window.fbq("track", "AddToCart", pixelEventData);
        console.log(`✓ Meta Pixel ${pixelId} Event Sent:`, pixelEventData);
      } catch (pixelError) {
        console.error(`Meta Pixel ${pixelId} Error:`, pixelError);
      }
    });
  } catch (error) {
    console.error("Meta Pixel Tracking Error:", error);
  }
};

/**
 * Get current user data from localStorage or form
 */
const getUserData = () => {
  try {
    const storedSession = localStorage.getItem("customer_session");
    if (!storedSession) return {};

    const session = JSON.parse(storedSession);
    return {
      email: session?.email || undefined,
      phone: session?.phone_number || session?.mobile || undefined,
      first_name: session?.first_name || undefined,
      last_name: session?.last_name || undefined,
      customer_id: session?.customer_id || undefined,
    };
  } catch (error) {
    console.error("Error getting user data:", error);
    return {};
  }
};

/**
 * Track Purchase event (call this after successful order)
 */
export const trackPurchase = (
  orderData,
  items = [],
  currencyCode = "INR"
) => {
  if (!orderData || !orderData.order_total) {
    console.warn("TrackingService: Invalid order data", orderData);
    return;
  }

  const userData = getUserData();

  // Track GA4 Purchase Event
  try {
    if (window.dataLayer) {
      window.dataLayer.push({
        event: "purchase",
        ecommerce: {
          transaction_id: orderData.order_id || "order_" + Date.now(),
          affiliation: "Naatu Sakkarai",
          value: orderData.order_total,
          tax: orderData.tax_amount || 0,
          shipping: orderData.shipping_amount || 0,
          currency: currencyCode,
          items: items.map((item) => ({
            item_id: item.product_id,
            item_name: item.product_name,
            item_brand: item.brand,
            item_category: item.category,
            price: item.price,
            quantity: item.quantity,
          })),
        },
      });
    }
  } catch (error) {
    console.error("GA4 Purchase Tracking Error:", error);
  }

  // Track Meta Pixel Purchase Event
  try {
    if (window.fbq) {
      const customEventName = `purchase_${orderData.order_id || 'unknown'}`;
      window.fbq("trackCustom", customEventName, {
        value: orderData.order_total,
        currency: currencyCode,
        contents: items.map((item) => ({
          id: item.product_id,
          name: item.product_name,
          brand: item.brand,
          category: item.category,
          price: item.price,
          quantity: item.quantity,
        })),
        content_type: "product",
        num_items: items.length,
        user_data: {
          email: userData.email,
          phone_number: userData.phone,
          address: {
            first_name: userData.first_name,
            last_name: userData.last_name,
          },
        },
      });
    }
  } catch (error) {
    console.error("Meta Pixel Purchase Tracking Error:", error);
  }

  console.log("✓ Purchase tracked:", {
    order_id: orderData.order_id,
    value: orderData.order_total,
    items: items.length,
  });
};

/**
 * Track View Content event (call on product page load)
 */
export const trackViewContent = (productData, currencyCode = "INR") => {
  if (!productData || !productData.product_id) {
    console.warn("TrackingService: Invalid product data", productData);
    return;
  }

  const price = productData.sp > 0 ? productData.sp : productData.mrp;

  // GA4
  try {
    if (window.dataLayer) {
      window.dataLayer.push({
        event: "view_item",
        ecommerce: {
          value: price,
          currency: currencyCode,
          items: [
            {
              item_id: productData.product_id,
              item_name: productData.product_name,
              item_brand: productData.brand_name || "",
              item_category: productData.category_name || "",
              price: price,
            },
          ],
        },
      });
    }
  } catch (error) {
    console.error("GA4 View Content Error:", error);
  }

  // Meta Pixel
  try {
    if (window.fbq) {
      window.fbq("track", "ViewContent", {
        content_name: productData.product_name,
        content_ids: [productData.product_id],
        content_type: "product",
        value: price,
        currency: currencyCode,
      });
    }
  } catch (error) {
    console.error("Meta Pixel View Content Error:", error);
  }
};

export default {
  trackAddToCart,
  trackPurchase,
  trackViewContent,
  getUserData,
};
